import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { eventsApi } from '../../api/eventsApi';
import { 
  DocumentChartBarIcon, 
  ChatBubbleLeftRightIcon, 
  ShareIcon, 
  QuestionMarkCircleIcon,
  ArrowPathIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import './event-modals.css';

const EventReportModal = ({ isOpen, onClose, event, onReportGenerated }) => {
  const [options, setOptions] = useState({
    chatbot: true,
    graph: true,
    qa: false,
    push_to_wordpress: false
  });
  const [questions, setQuestions] = useState('');
  // MARKO KOD
  const [faiconPostId, setFaiconPostId] = useState('');
  // MARKO KOD KRAJ
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setOptions({ chatbot: true, graph: true, qa: false, push_to_wordpress: false });
      setQuestions('');
      // MARKO KOD
      setFaiconPostId('');
      // MARKO KOD KRAJ
    }
  }, [isOpen]);

  const handleOptionChange = (option) => {
    setOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');

    try {
      // Parse questions (one per line)
      const questionsList = questions
        .split('\n')
        .map(q => q.trim())
        .filter(q => q.length > 0);

      // MARKO KOD
      const payload = {
        questions: questionsList,
        chatbot: options.chatbot,
        qa: options.qa && questionsList.length > 0,
        graph: options.graph,
        push_to_wordpress: options.push_to_wordpress
      };
      if (faiconPostId.trim()) {
        payload.faicon_post_id = faiconPostId.trim();
      }
      await eventsApi.generateReport(event.id, payload);
      // MARKO KOD KRAJ

      // Close modal and notify parent that generation started
      if (onReportGenerated) {
        onReportGenerated('started');
      }
      onClose();
      
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to start report generation');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Generate Event Report" size="lg" className="event-modal">
      <form onSubmit={handleSubmit} className="event-modal-form">
        <div className="event-modal-intro">
          <strong>{event?.title || 'Event report'}</strong>
          <span>Choose which assets should be generated from this event's transcripts.</span>
        </div>

        <div className="event-modal-section">
          <span className="event-modal-section-title">Report options</span>
          <div className="event-modal-option-list">
            <label className="event-modal-option">
              <input
                type="checkbox"
                checked={options.chatbot}
                onChange={() => handleOptionChange('chatbot')}
                disabled={loading}
              />
              <div>
                <div className="event-modal-option-head">
                  <ChatBubbleLeftRightIcon />
                  <strong>Create chatbot</strong>
                </div>
                <p>Create an AI chatbot that can answer questions about this event's transcripts.</p>
              </div>
            </label>

            <label className="event-modal-option">
              <input
                type="checkbox"
                checked={options.graph}
                onChange={() => handleOptionChange('graph')}
                disabled={loading}
              />
              <div>
                <div className="event-modal-option-head">
                  <ShareIcon />
                  <strong>Generate knowledge graph</strong>
                </div>
                <p>Create an interactive graph showing speakers, arguments, and topics.</p>
              </div>
            </label>

            <label className="event-modal-option">
              <input
                type="checkbox"
                checked={options.qa}
                onChange={() => handleOptionChange('qa')}
                disabled={loading}
              />
              <div>
                <div className="event-modal-option-head">
                  <QuestionMarkCircleIcon />
                  <strong>Q&A analysis</strong>
                </div>
                <p>Analyze specific questions across all session transcripts.</p>
              </div>
            </label>

            <label className="event-modal-option">
              <input
                type="checkbox"
                checked={options.push_to_wordpress}
                onChange={() => handleOptionChange('push_to_wordpress')}
                disabled={loading}
              />
              <div>
                <div className="event-modal-option-head">
                  <GlobeAltIcon />
                  <strong>Publish to WordPress</strong>
                </div>
                <p>Automatically publish the event report to WordPress after generation.</p>
              </div>
            </label>
          </div>
        </div>

        <label className="event-modal-field" htmlFor="faicon-post-id">
          <span>Faicon Event ID (optional)</span>
          <input
            id="faicon-post-id"
            type="text"
            value={faiconPostId}
            onChange={(e) => setFaiconPostId(e.target.value)}
            className="event-modal-control"
            placeholder=""
            disabled={loading}
          />
          <small>
            If you plan to send this report to Faicon, enter the event ID here. It will be used as <code>wpPostId</code> in the report JSON.
          </small>
        </label>

        {options.qa && (
          <label className="event-modal-field" htmlFor="questions">
            <span>Questions for analysis</span>
            <textarea
              id="questions"
              value={questions}
              onChange={(e) => setQuestions(e.target.value)}
              rows={5}
              className="event-modal-control"
              placeholder="Enter one question per line, e.g.:&#10;What were the main conclusions?&#10;What topics received the most attention?&#10;Were there any disagreements?"
              disabled={loading}
            />
            <small>Enter one question per line. The AI will search all session transcripts to find relevant discussions.</small>
          </label>
        )}

        {error && (
          <div className="event-modal-error">{error}</div>
        )}

        <div className="event-modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="event-modal-btn"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="event-modal-btn event-modal-btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <ArrowPathIcon className="event-modal-spin" />
                Starting...
              </>
            ) : (
              <>
                <DocumentChartBarIcon />
                Generate report
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EventReportModal;
